/**
 * Blog Post Scroll Animations
 * Handles scroll-triggered animations for blog post content including separators, headings, and paragraphs
 */

class BlogPostAnimations {
    constructor() {
        this.observer = null;
        this.elementsToAnimate = [];
        this.isScrolling = false;
        this.scrollTimeout = null;
        
        // Parallax elements for smooth scrolling effects
        this.parallaxElements = [];
        
        this.init();
    }

    init() {
        if (typeof window !== 'undefined') {
            // Wait for DOM to be fully loaded
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
            } else {
                this.setupAnimations();
            }
        }
    }

    setupAnimations() {
        // Only run on blog post pages
        const blogPostContent = document.getElementById('blog-post-article-content');
        if (!blogPostContent) {
            console.log('ℹ️ Blog post animations: Not a blog post page');
            return;
        }

        this.setupIntersectionObserver();
        this.collectAnimationElements();
        this.setupParallaxScrolling();
        this.setupEnhancedMetadata();
        this.setupShareButtonInteractions();
        this.setupEmojiAnimations();
        this.setupFeaturedImageCaption();

        console.log(`✅ Blog post animations initialized with ${this.elementsToAnimate.length} elements`);
    }

    setupIntersectionObserver() {
        // Completely disabled - no intersection observer needed
        this.observer = null;
    }

    collectAnimationElements() {
        const blogContent = document.getElementById('blog-post-article-content');
        if (!blogContent) return;

        // Completely disable animation collection and cleanup any existing inline styles
        // Find ALL elements that may have had inline styles applied
        const allElements = blogContent.querySelectorAll('*');
        
        allElements.forEach(element => {
            // Remove all animation-related inline styles
            element.style.opacity = '';
            element.style.transform = '';
            element.style.transitionDelay = '';
            element.style.animation = '';
            element.style.transition = '';
        });

        // No elements to animate - completely disabled
        this.elementsToAnimate = [];
        this.parallaxElements = [];
    }

    calculateStaggerDelay(element, index) {
        const tagName = element.tagName.toLowerCase();
        const baseDelays = {
            'hr': 0,           // No delay for separators
            'h1': 100,         // Headings first
            'h2': 150,
            'h3': 200,
            'h4': 250,
            'p': 300,          // Then paragraphs
            'blockquote': 400, // Then quotes
            'li': 500          // Finally list items
        };
        
        const baseDelay = baseDelays[tagName] || 300;
        const staggerDelay = index * 50; // 50ms between similar elements
        
        return baseDelay + staggerDelay;
    }

    getElementType(element) {
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'hr') return 'separator';
        if (['h1', 'h2', 'h3', 'h4'].includes(tagName)) return 'heading';
        if (tagName === 'p') return 'paragraph';
        if (tagName === 'blockquote') return 'quote';
        if (tagName === 'li') return 'listitem';
        return 'element';
    }

    animateElement(element) {
        // Completely disabled - no animation processing
        return;
    }

    handleSpecialAnimations(element, type) {
        switch (type) {
            case 'separator':
                this.animateSeparator(element);
                break;
            case 'heading':
                this.animateHeading(element);
                break;
            case 'paragraph':
                this.animateParagraph(element);
                break;
            case 'quote':
                this.animateQuote(element);
                break;
            case 'listitem':
                this.animateListItem(element);
                break;
        }
    }

    animateSeparator(element) {
        // No animations - just ensure clean state
        element.style.opacity = '';
        element.style.transform = '';
        element.style.transitionDelay = '';
        element.style.animation = '';
    }

    animateHeading(element) {
        // Add subtle bounce effect for headings
        element.style.animation = 'headingBounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    }

    animateParagraph(element) {
        // Fade in with slight upward motion
        const words = element.textContent.split(' ');
        if (words.length > 20) {
            // For long paragraphs, add subtle typing effect
            element.style.animation = 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        }
    }

    animateQuote(element) {
        // Add subtle rotation correction
        setTimeout(() => {
            element.style.transform = 'translateX(0) rotate(0deg) scale(1.01)';
        }, 100);
    }

    animateListItem(element) {
        // Add subtle slide-in effect
        element.style.animation = 'slideInLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    }

    setupParallaxScrolling() {
        // Completely disabled - no parallax scrolling effects
        // This prevents the opacity and transform changes on HR elements
        return;
    }

    updateParallax() {
        // Completely disabled - no parallax effects
        // This was causing the opacity and transform changes on HR elements
        return;
    }

    setupEnhancedMetadata() {
        // Set reading experience label based on reading time
        const readingLabel = document.getElementById('reading-experience-label');
        if (readingLabel) {
            const readingTime = parseInt(readingLabel.dataset.readingTime) || 0;
            let label = 'Insightful';
            
            if (readingTime <= 2) {
                label = 'Quick Read';
            } else if (readingTime <= 7) {
                label = 'Deep Dive';
            } else if (readingTime >= 8) {
                label = 'Long Form';
            }
            
            readingLabel.textContent = label;
        }
    }

    setupShareButtonInteractions() {
        const shareButton = document.getElementById('blog-post-share-button');
        if (!shareButton) return;

        // Click ripple effect
        shareButton.addEventListener('click', (e) => {
            e.preventDefault();
            
            // Create ripple effect
            const rippleContainer = shareButton.querySelector('.absolute.inset-0.rounded-full.overflow-hidden > div');
            if (rippleContainer) {
                // Reset animation
                rippleContainer.style.transition = 'none';
                rippleContainer.style.opacity = '0';
                rippleContainer.style.transform = 'scale(0)';
                
                // Force reflow
                rippleContainer.offsetHeight;
                
                // Start animation
                rippleContainer.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
                rippleContainer.style.opacity = '0.3';
                rippleContainer.style.transform = 'scale(4)';
                
                // Fade out
                setTimeout(() => {
                    rippleContainer.style.opacity = '0';
                }, 400);
                
                // Reset for next click
                setTimeout(() => {
                    rippleContainer.style.transform = 'scale(0)';
                }, 600);
            }
            
            // Share functionality
            this.handleShare();
        });

        // Enhanced hover effects
        shareButton.addEventListener('mouseenter', () => {
            const icon = shareButton.querySelector('svg');
            if (icon) {
                icon.style.transform = 'rotate(12deg) scale(1.1)';
            }
        });

        shareButton.addEventListener('mouseleave', () => {
            const icon = shareButton.querySelector('svg');
            if (icon) {
                icon.style.transform = 'rotate(0deg) scale(1)';
            }
        });
    }

    handleShare() {
        const url = window.location.href;
        const title = document.title;
        
        // Use Web Share API if available
        if (navigator.share) {
            navigator.share({
                title: title,
                url: url
            }).catch(err => {
                console.log('Share cancelled or failed:', err);
                this.fallbackShare(url, title);
            });
        } else {
            this.fallbackShare(url, title);
        }
    }

    fallbackShare(url, title) {
        // Create share menu
        const shareMenu = document.createElement('div');
        shareMenu.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center';
        shareMenu.innerHTML = `
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Share this article</h3>
                <div class="space-y-3">
                    <button onclick="navigator.clipboard.writeText('${url}'); this.textContent='Copied!'; setTimeout(() => this.textContent='Copy Link', 2000);" 
                            class="w-full flex items-center gap-3 p-3 text-left bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                        <span class="text-gray-700 dark:text-gray-300">Copy Link</span>
                    </button>
                    <button onclick="window.open('https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}', '_blank')" 
                            class="w-full flex items-center gap-3 p-3 text-left bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                        <span class="text-gray-700 dark:text-gray-300">Share on Twitter</span>
                    </button>
                </div>
                <button onclick="this.closest('.fixed').remove()" 
                        class="mt-4 w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                    Close
                </button>
            </div>
        `;
        
        // Close on background click
        shareMenu.addEventListener('click', (e) => {
            if (e.target === shareMenu) {
                shareMenu.remove();
            }
        });
        
        document.body.appendChild(shareMenu);
    }

    setupEmojiAnimations() {
        // Find all emojis in the blog content
        const blogContent = document.getElementById('blog-post-article-content');
        if (!blogContent) return;

        // Regex to match emoji characters (basic implementation)
        const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;

        // Walk through all text nodes to find emojis
        const walker = document.createTreeWalker(
            blogContent,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
            if (emojiRegex.test(node.textContent)) {
                textNodes.push(node);
            }
        }

        // Process each text node containing emojis
        textNodes.forEach((textNode, index) => {
            const content = textNode.textContent;
            const matches = content.match(emojiRegex);
            
            if (matches && matches.length > 0) {
                // Replace text node with HTML containing animated emojis
                const newContent = content.replace(emojiRegex, (emoji) => {
                    return `<span class="animated-emoji" data-emoji="${emoji}">${emoji}</span>`;
                });
                
                // Create a temporary element to hold the new content
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = newContent;
                
                // Replace the text node with the new elements
                const parent = textNode.parentNode;
                while (tempDiv.firstChild) {
                    parent.insertBefore(tempDiv.firstChild, textNode);
                }
                parent.removeChild(textNode);
            }
        });

        // Set up animation intervals for each emoji
        const animatedEmojis = blogContent.querySelectorAll('.animated-emoji');
        animatedEmojis.forEach((emoji, index) => {
            // Stagger the animation start times
            const delay = (index * 1000) + Math.random() * 2000; // Random delay between 0-2s per emoji
            
            setTimeout(() => {
                this.startEmojiAnimation(emoji);
            }, delay);
        });

        console.log(`✅ Emoji animations initialized for ${animatedEmojis.length} emojis`);
    }

    startEmojiAnimation(emojiElement) {
        const animateEmoji = () => {
            // Subtle shake animation
            emojiElement.style.transition = 'transform 0.1s ease-in-out';
            
            const shakeSequence = [
                { transform: 'translateX(0px)', duration: 0 },
                { transform: 'translateX(-1px)', duration: 100 },
                { transform: 'translateX(1px)', duration: 100 },
                { transform: 'translateX(-1px)', duration: 100 },
                { transform: 'translateX(1px)', duration: 100 },
                { transform: 'translateX(0px)', duration: 100 }
            ];

            let currentStep = 0;
            const executeStep = () => {
                if (currentStep < shakeSequence.length) {
                    const step = shakeSequence[currentStep];
                    emojiElement.style.transform = step.transform;
                    
                    setTimeout(() => {
                        currentStep++;
                        executeStep();
                    }, step.duration);
                } else {
                    // Reset and schedule next animation
                    emojiElement.style.transform = 'translateX(0px)';
                    setTimeout(animateEmoji, 7000); // Repeat every 7 seconds
                }
            };

            executeStep();
        };

        // Start the animation cycle
        animateEmoji();
    }

    setupFeaturedImageCaption() {
        // Handle featured image caption
        const featuredSection = document.getElementById('blog-post-featured-image');
        if (!featuredSection) {
            console.log('ℹ️ No featured image section found');
            return;
        }

        const featuredImg = featuredSection.querySelector('img');
        const captionDiv = document.getElementById('featured-image-caption');
        const captionText = document.getElementById('featured-image-caption-text');

        console.log('🖼️ Featured image caption debug:', {
            featuredImg: !!featuredImg,
            altText: featuredImg ? featuredImg.alt : 'No image',
            captionDiv: !!captionDiv,
            captionText: !!captionText
        });

        if (featuredImg && captionDiv && captionText && featuredImg.alt && featuredImg.alt.trim()) {
            console.log('📝 Setting featured image caption:', featuredImg.alt);
            captionText.textContent = featuredImg.alt.trim();
            captionDiv.style.display = 'block';
            console.log('✅ Featured image caption displayed successfully');
        } else {
            console.log('ℹ️ Featured image caption not set - missing elements or alt text');
        }
    }

    // Cleanup method
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        
        // Remove scroll listeners (already disabled but just in case)
        window.removeEventListener('scroll', this.updateParallax);
        
        console.log('Blog post animations cleaned up');
    }
}

// CSS animations to be added dynamically
const animationStyles = `
@keyframes headingBounce {
    0% { transform: translateX(-30px) scale(0.9); }
    60% { transform: translateX(5px) scale(1.02); }
    100% { transform: translateX(0) scale(1); }
}

@keyframes fadeInUp {
    0% { 
        opacity: 0; 
        transform: translateY(15px); 
    }
    100% { 
        opacity: 1; 
        transform: translateY(0); 
    }
}

@keyframes slideInLeft {
    0% { 
        opacity: 0; 
        transform: translateX(-10px) scale(0.95); 
    }
    100% { 
        opacity: 1; 
        transform: translateX(0) scale(1); 
    }
}
`;

// Inject animation styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = animationStyles;
    document.head.appendChild(styleSheet);
}

// Export for use in main theme
export default BlogPostAnimations;